import { memo, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useClickAway } from 'react-use';
import { useGetRequest } from '../../../../hooks/useGetRequest';
import {
  getSides,
  checkDoctorDept,
  getDragDropStyle,
  isHiddenInPrint,
  itemSettingStyle,
  itemTitleStyle,
  lineHeightStyle,
} from '../../../../utils/helpers';
import CommonDeleteBtn from '../commonDeleteBtn';
import InvestigationUnit from '../investigationUnit';
import SelectedItemSearch from './selectedItemSearch';

const Investigation = ({
  element,
  prescriptionItems,
  setShowInvestigation,
  deletePrescriptionItem,
  selectedInvestigations,
  setSelectedInvestigations,
  isHistoryPage = false,
}) => {
  const userInfo = useSelector((state) => state.authReducer.data);
  const specialties = useSelector(
    (state) => state.specialtyReducer.specialties,
  );
  const [isEdit, setIsEdit] = useState('');
  const [units, setUnits] = useState([]);

  useEffect(() => {
    getUnits();
  }, []);

  const { isLoading: isUnitLoading, refetch: getUnits } = useGetRequest(
    'getUnit',
    `units`,
    (data) => {
      setUnits(data.data);
    },
    (e) => {
      console.log(e);
    },
  );

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedItems = reorder(
      selectedInvestigations,
      result.source.index,
      result.destination.index,
    );
    setSelectedInvestigations(reorderedItems);
  };

  // this function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  // const handleInvestigationExtras = (item, fieldName, val) => {
  //   const objIndex = selectedInvestigations.findIndex(
  //     (investigation) => investigation.name == item.name,
  //   );
  //   selectedInvestigations[objIndex][fieldName] = val;
  //   setSelectedInvestigations([...selectedInvestigations]);
  // };

  const handleInvestigationExtras = (item, fieldName, val) => {
    const objIndex = selectedInvestigations.findIndex(
      (investigation) => investigation.name === item.name,
    );

    if (objIndex !== -1) {
      const updatedInvestigations = selectedInvestigations.map(
        (investigation, index) =>
          index === objIndex
            ? { ...investigation, [fieldName]: val }
            : investigation,
      );

      setSelectedInvestigations(updatedInvestigations);
    }
  };

  const ref = useRef(null);
  useClickAway(ref, (e) => {
    setIsEdit('');
  });

  const editMode = (index, item) => {
    return (
      <Row ref={ref} className="selected-row-edit" key={item.name}>
        <Col md={4} className="pl-0 pr-0">
          {/* {item.name} */}
          <SelectedItemSearch
            apiUrl="investigations"
            selectedItemName={item.name}
            selectedItems={selectedInvestigations}
            selectedItemsAction={setSelectedInvestigations}
          />
        </Col>
        <Col md={2} className="pr-0">
          <Form.Select
            className="form-control form-control-sm"
            defaultValue={item?.side}
            onChange={(e) =>
              handleInvestigationExtras(item, 'side', e.target.value)
            }
          >
            <option value="">Side</option>
            {getSides(
              checkDoctorDept(
                'opthalmology',
                userInfo?.speciality[0],
                specialties,
              ),
            ).map((side) =>
              Object.entries(side).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              )),
            )}
          </Form.Select>
        </Col>
        <Col className="pl-0 pr-0">
          <Form.Control
            size="sm"
            min={0}
            type="text"
            defaultValue={item.instruction}
            placeholder="Add instruction"
            onChange={(e) =>
              handleInvestigationExtras(item, 'instruction', e.target.value)
            }
          />
        </Col>
        <Col className="pl-0 pr-0">
          <Form.Control
            size="sm"
            type="text"
            defaultValue={item.result}
            placeholder="Add result"
            onChange={(e) =>
              handleInvestigationExtras(item, 'result', e.target.value)
            }
          />
        </Col>
        <Col md={2} className="inv-parent pr-0">
          <InvestigationUnit
            index={index}
            units={units}
            item={item}
            selectedInvestigations={selectedInvestigations}
            action={setSelectedInvestigations}
          />
        </Col>
        <Col md="1" className="text-right pr-0">
          <i
            className="fa fa-times-circle"
            aria-hidden="true"
            onClick={() => setIsEdit('')}
          ></i>
        </Col>
      </Row>
    );
  };

  return (
    <div
      className={`row item-row ${
        selectedInvestigations.length ? 'data-item' : 'no-data-item'
      }`}
      key={`investigations`}
    >
      <span
        className={`prescription-item ${
          isHiddenInPrint(prescriptionItems, 'investigation')
            ? 'item-title-hide'
            : ''
        }`}
        style={itemTitleStyle(
          prescriptionItems?.items?.investigation?.titleStyle || {},
        )}
        {...(!isHistoryPage
          ? {
              onClick: (e) => {
                e.stopPropagation();
                setShowInvestigation(true);
              },
            }
          : {})}
      >
        {element?.alterName?.length ? (
          <span>{element?.alterName}</span>
        ) : (
          <span>Investigations</span>
        )}
      </span>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <ul
              className="sub-child-list drag-drop"
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={lineHeightStyle('investigation', prescriptionItems)}
            >
              {selectedInvestigations?.map((item, index) => (
                <Draggable
                  key={item.name}
                  draggableId={item.name}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <li
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...getDragDropStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style,
                        ),
                        position: 'relative',
                      }}
                    >
                      <div onClick={() => setIsEdit(index)}>
                        <span
                          className="text-left"
                          style={itemSettingStyle(
                            prescriptionItems?.items?.investigation
                              ?.itemStyle || {},
                          )}
                        >
                          {item.name}
                          {item?.side ? ` - ${item.side.toUpperCase()}` : ''}
                          {item?.result && (
                            <span className="pl-2">
                              -{' '}
                              <i>
                                {item.result} {item?.unit}
                              </i>
                            </span>
                          )}
                        </span>
                        {!isHistoryPage && (
                          <CommonDeleteBtn
                            action={deletePrescriptionItem}
                            itemName={'investigation'}
                            delId={item.name}
                          />
                        )}
                      </div>
                      {item?.instruction ? (
                        <div
                          className="pl-2"
                          style={itemSettingStyle(
                            prescriptionItems?.items?.investigation
                              ?.subItemStyle || {},
                          )}
                        >
                          - {item.instruction}
                        </div>
                      ) : (
                        ''
                      )}
                      {index === isEdit && editMode(index, item)}
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default memo(Investigation);
